/**
 * What a drop zone (`FileAttachmentZone`, `SingleFileZone`) says when an
 * upload fails: a sentence for the toast and the failed row's tooltip, and the
 * original technical text for the toast's «Ver detalle técnico» fold.
 *
 * The zones take a `uploadErrorText` prop so the tenant translates the
 * server's refusal (`{code, params, message}`, e.g. `file_type_invalid` or
 * `file_too_large_max`) through its own `t()`. This is the default — the
 * Admin's, which is Spanish-only: the refusal's own Spanish `message`, a
 * cut connection in plain words, and the caller's label for anything else.
 * A raw text is never the message there either when it can be avoided.
 */
import { isFileChangedError } from '../fileUpload';
import { isNetworkUploadError, UploadRefusedError } from './retry';

export interface UploadErrorText {
    /** The sentence shown to the person. */
    text: string;
    /** The original technical text, folded under «Ver detalle técnico». */
    detail?: string | null;
}

export type UploadErrorTextFn = (err: unknown, fileName: string) => UploadErrorText;

/** Spanish — the Admin's text for an upload the network gave up on. */
export const UPLOAD_NETWORK_ERROR_ES =
    'No se ha podido subir el archivo: se ha cortado la conexión o el servidor no responde. Vuelve a intentarlo.';

export function defaultUploadErrorText(
    err: unknown,
    fileName: string,
    fallback: (name: string) => string,
): UploadErrorText {
    if (isFileChangedError(err)) return { text: (err as Error).message };
    if (err instanceof UploadRefusedError) {
        const d = err.detail;
        if (d && typeof d === 'object') {
            const message = (d as { message?: unknown }).message;
            if (typeof message === 'string' && message.trim()) return { text: message.trim() };
        }
        if (typeof d === 'string' && d.trim()) return { text: d.trim() };
    }
    if (isNetworkUploadError(err)) return { text: UPLOAD_NETWORK_ERROR_ES, detail: err.message || null };
    return { text: fallback(fileName), detail: err instanceof Error && err.message ? err.message : null };
}
